import { Form, useFormikContext } from "formik";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import ErrorBoundary from "web/Layout/ErrorBoundary";

import useIsDashboardHeaderEnabled from "web/hooks/useIsDashboardHeaderEnabled";
import usePrevious from "web/hooks/usePrevious/usePrevious";

import urls from "web/constants/urls";

import { useAppContext } from "web/context/app";
import { closeNavigation } from "web/features/app/appSlice";

import AutocompleteDesktop from "./Desktop/Autocomplete";
import SearchFieldDesktop from "./Desktop/SearchField";
import AutocompleteMobile from "./Mobile/Autocomplete";
import SearchFieldMobile from "./Mobile/SearchField";
import classesDesktop from "./searchDesktop.scss";
import classesMobile from "./searchMobile.scss";
import useMultisportSearch from "./utils/useMultisportSearch";
import useSearchOverlayScrollLock from "./utils/useSearchOverlayScrollLock";

const timeout = 500;

interface ISearchAutocompleteSearchProps {
  className: string;
  name: string;
  pathname: string;
  search: string;
  onClick: () => void;
  variant?: "default" | "header";
}

const SearchAutocompleteSearch: FC<ISearchAutocompleteSearchProps> = ({
  className,
  name,
  pathname,
  search,
  onClick = null,
  variant = "default",
}) => {
  const dispatch = useDispatch();
  const setTimeOutId = useRef<NodeJS.Timeout>();
  const hasUserEditedSearchRef = useRef(false);
  const previousLocationRef = useRef({ pathname, search });
  const [autocompleteValue, setAutocompleteValue] = useState("");
  const { values, dirty, initialValues, setFieldValue } = useFormikContext<{
    [name: string]: string;
  }>();
  const { isMobile } = useAppContext();
  const classes = isMobile ? classesMobile : classesDesktop;
  const Autocomplete = isMobile ? AutocompleteMobile : AutocompleteDesktop;
  const SearchField = isMobile ? SearchFieldMobile : SearchFieldDesktop;
  const initialValue = initialValues[name as keyof typeof initialValues];
  const currentValue = values[name as keyof typeof values];
  const previousValue = usePrevious(currentValue);
  const closeAutocomplete = useCallback(() => {
    setAutocompleteValue("");
  }, [setAutocompleteValue]);
  const closeAutocompleteWithOnClick = useCallback(() => {
    if (typeof onClick === "function") {
      onClick();
    }
    dispatch(closeNavigation());
    closeAutocomplete();
  }, [closeAutocomplete, onClick]);
  const isOnSearchResultsPage = pathname === urls.search;

  useEffect(() => {
    clearTimeout(setTimeOutId.current);

    if (previousValue !== undefined && previousValue !== currentValue) {
      hasUserEditedSearchRef.current = true;
    }

    const hasUserEditedSearch = hasUserEditedSearchRef.current;
    const shouldOpenAutocomplete = isOnSearchResultsPage
      ? Boolean(currentValue && (dirty || hasUserEditedSearch))
      : Boolean(
          (currentValue && dirty) ||
            (previousValue !== currentValue && currentValue === initialValue)
        );

    if (
      currentValue === initialValue &&
      !dirty &&
      !(isOnSearchResultsPage && hasUserEditedSearch)
    ) {
      if (isOnSearchResultsPage) {
        closeAutocomplete();
      } else {
        setAutocompleteValue(currentValue);
      }
    } else if (shouldOpenAutocomplete) {
      setTimeOutId.current = setTimeout(() => {
        setAutocompleteValue(currentValue);
      }, timeout);
    } else {
      closeAutocomplete();
    }

    return () => {
      clearTimeout(setTimeOutId.current);
    };
  }, [
    currentValue,
    previousValue,
    initialValue,
    dirty,
    closeAutocomplete,
    isOnSearchResultsPage,
  ]);

  useEffect(() => {
    const locationChanged =
      previousLocationRef.current.pathname !== pathname ||
      previousLocationRef.current.search !== search;
    previousLocationRef.current = { pathname, search };

    if (locationChanged) {
      hasUserEditedSearchRef.current = false;
    }

    clearTimeout(setTimeOutId.current);
    if (pathname === urls.search) {
      if (locationChanged) {
        closeAutocomplete();
      }
      return;
    }
    setFieldValue(name, "");
    closeAutocomplete();
  }, [pathname, setFieldValue, closeAutocomplete, search, name]);

  const { isDashboardHeaderEnabled } = useIsDashboardHeaderEnabled();

  useMultisportSearch({
    searchValue: autocompleteValue,
    searchPhrase: currentValue,
  });

  useSearchOverlayScrollLock({
    isMobile,
    variant,
    isOverlayOpen: Boolean(autocompleteValue),
    onCloseOverlay: closeAutocomplete,
  });

  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }

    const getFocusedSearchInput = () => {
      const { activeElement } = document;
      if (
        activeElement instanceof HTMLInputElement &&
        activeElement.dataset.t1 === "search_input"
      ) {
        return activeElement;
      }
      return null;
    };

    const blurSearchInput = () => {
      getFocusedSearchInput()?.blur();
    };

    const blurSearchInputOnOutsideTouch = (event: TouchEvent) => {
      const searchInput = getFocusedSearchInput();
      if (!searchInput) {
        return;
      }

      const { target } = event;
      if (target instanceof Node && searchInput.contains(target)) {
        return;
      }

      searchInput.blur();
    };

    document.addEventListener("touchstart", blurSearchInputOnOutsideTouch, {
      capture: true,
      passive: true,
    });
    window.addEventListener("scroll", blurSearchInput, true);

    return () => {
      document.removeEventListener(
        "touchstart",
        blurSearchInputOnOutsideTouch,
        true
      );
      window.removeEventListener("scroll", blurSearchInput, true);
    };
  }, [isMobile]);

  const autocompleteClassName = (() => {
    if (isMobile && variant === "header")
      return classes.autocompleteHeaderOverlay;
    return isDashboardHeaderEnabled
      ? classes.autocompleteDashboard
      : classes.autocomplete;
  })();

  return (
    <div className={className || classes.root}>
      <div className={classes.wrapper} data-t1="search">
        <Form className={classes.form} data-t1="search_form">
          <SearchField
            name={name}
            currentValue={currentValue}
            variant={variant}
          />
        </Form>
        {autocompleteValue ? (
          <div className={autocompleteClassName}>
            <ErrorBoundary>
              <Autocomplete
                searchPhrase={autocompleteValue.replace("&", "")}
                closeAction={closeAutocompleteWithOnClick}
                className={classes.wrapper}
              />
            </ErrorBoundary>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchAutocompleteSearch;
